/**
 * summarizeDraft — a one-line, plain-language read-back of a view draft's
 * clause rows ("Shows the Payments team, grouped by @@team, coloured by
 * @@criticality, drawn as a board."). The composer shows it above the sections
 * so the user reads what the view DOES, not the clause fragments. Pure over the
 * same `ClauseRow[]` the panel edits — no drift from what `rowText` writes.
 */
import { atomText, rowText, selTermComplete, type ClauseRow, type SelTerm } from "./clauseModel.js";

const REP_KINDS = ["flow", "table", "matrix", "grid"] as const;

/**
 * One selector clause's read-back phrase (null = nothing to say). A plain union
 * of atoms narrates as its members; anything with structure — a group, a
 * predicate, an arrow — narrates as "a filtered set", since the point of the
 * line is what the view DOES, not a re-reading of the selector text (the row
 * itself already shows that verbatim).
 */
function selectorPhrase(rows: readonly ClauseRow[], kind: "show" | "hide" | "focus", verb: string): string | null {
  const sels = rows
    .filter((r): r is Extract<ClauseRow, { sel: SelTerm }> => r.kind === kind)
    .map((r) => r.sel)
    .filter(selTermComplete);
  if (!sels.length) return null;
  // Flatten each clause's top-level union into its members; a member that is
  // not a plain atom makes the whole phrase read as filtered.
  const members = sels.flatMap((t) => (t.kind === "or" ? t.terms : [t])).filter(selTermComplete);
  const atoms = members.filter((t): t is Extract<SelTerm, { kind: "atom" }> => t.kind === "atom");
  const structured = atoms.length !== members.length;
  if (atoms.length === 0) return `${verb} a filtered set`;
  const allOnly = atoms.length === 1 && atoms[0]!.atom.kind === "all";
  const base = allOnly ? "everything" : atoms.map((t) => atomText(t.atom)).join(" or ");
  return `${verb} ${base}${structured ? " (refined)" : ""}`;
}

/** Render a draft's rows to a single human sentence (empty when nothing to say). */
export function summarizeDraft(rows: readonly ClauseRow[]): string {
  const parts: string[] = [];

  const showP = selectorPhrase(rows, "show", "shows");
  if (showP) parts.push(showP);
  const focusP = selectorPhrase(rows, "focus", "focuses");
  if (focusP) parts.push(focusP);
  const hideP = selectorPhrase(rows, "hide", "hides");
  if (hideP) parts.push(hideP);

  const group = rows.find((r) => r.kind === "groupBy" && (r.key || r.scope?.trim()));
  if (group && group.kind === "groupBy") {
    parts.push(group.scope?.trim() ? `grouped inside ${group.scope.trim()}` : `grouped by @@${group.key}`);
  }
  const on = rows.find((r) => r.kind === "on" && r.plane);
  if (on && on.kind === "on") parts.push(`on plane ${on.plane}`);

  const color = rows.find((r) => r.kind === "color" && r.key);
  if (color && color.kind === "color") parts.push(`coloured by @@${color.key}`);
  const styleN = rows.filter((r) => r.kind === "style" && rowText(r) !== null).length;
  if (styleN) parts.push(`${styleN} style rule${styleN > 1 ? "s" : ""}`);

  const rep = rows.find((r) => (REP_KINDS as readonly string[]).includes(r.kind));
  parts.push(`drawn as a ${rep ? rep.kind : "board"}`);

  // Nothing worth reading back = the default "shows everything on a board" —
  // strip those two default phrases and, if nothing meaningful is left, stay
  // silent (an unconfigured draft shouldn't narrate itself).
  const meaningful = parts.filter((p) => p !== "shows everything" && p !== "drawn as a board");
  if (meaningful.length === 0) return "";
  const s = parts.join(", ");
  return s.charAt(0).toUpperCase() + s.slice(1) + ".";
}
